/**
 * One server on the rail (Console v2 §3.1): the pill, the tile, the badge,
 * a click opens it, its menu (right-click, Shift+F10 or a long press) has
 * Move up / Move down, the notification items and Leave or Delete. Servers
 * you made or joined drag into the order you like (Discord); the official
 * server stays first.
 */
import type { Snowflake } from '@neosocial/protocol';
import { useLayout } from '@neosocial/ui/layout';
import { useState } from 'react';
import type React from 'react';
import { create } from 'zustand';
import { useT } from '../i18n';
import { guildNotifyItems } from '../notify/menus';
import { useData, useDataShallow } from '../store/data';
import { guildUnread, officialGuildId, railGuildIds, useActiveGuildId } from '../store/selectors';
import { goGuild, useUi } from '../store/ui';
import { openMenu, type Anchor, type MenuItem } from '../ui/overlay';
import { GuildIcon } from './GuildIcon';
import { serverExitItems } from './menu';
import { moveGuild } from './order';

const DRAG_TYPE = 'application/x-ns-guild';

/** The rail's drag: which server is lifted, and where it would land. */
const useRailDrag = create<{
  id: Snowflake | null;
  over: Snowflake | null;
  after: boolean;
}>(() => ({ id: null, over: null, after: false }));

const withSep = (items: MenuItem[]): MenuItem[] => (items.length ? [{ sep: true }, ...items] : []);

function menuPoint(e: React.MouseEvent<HTMLElement>): Anchor {
  return e.clientX || e.clientY ? { x: e.clientX, y: e.clientY } : e.currentTarget;
}

/** The movable servers (all but the official one), in rail order. */
function movable(): Snowflake[] {
  const d = useData.getState();
  const official = officialGuildId(d);
  return railGuildIds(d).filter((id) => id !== official);
}

export function RailGuild({ guildId }: { guildId: Snowflake }) {
  const { t, fmt } = useT();
  const home = useUi((u) => u.hub?.kind === 'home');
  const open = useActiveGuildId();
  const sel = !home && open === guildId;
  const official = useData((d) => officialGuildId(d) === guildId);
  const name = useData((d) => d.guilds[guildId]?.name ?? '');
  const hasIcon = useData((d) => !!d.guilds[guildId]?.icon);
  const unread = useDataShallow((d) => guildUnread(d, guildId));
  const drag = useRailDrag((s) => (s.over === guildId ? (s.after ? 'after' : 'before') : null));
  const lifted = useRailDrag((s) => s.id === guildId);
  const [menuOpen, setMenuOpen] = useState(false);
  // a mouse drags; a touch screen's long press opens the menu (Move up / Move down)
  const pointer = useLayout().pointer;
  const canDrag = !official && pointer === 'fine';
  // the badges are drawn beside the button: their counts go in its name
  const label =
    (official ? `${name}, ${t('hub.official')}` : name) +
    (!sel && unread.mentions
      ? ', ' + t('hub.ch.mentions', { n: unread.mentions })
      : !sel && unread.unread
        ? ', ' + t('hub.ch.unread')
        : '');
  const items = (): MenuItem[] => {
    const list = movable();
    const at = list.indexOf(guildId);
    return [
      { header: name },
      ...(official
        ? []
        : [
            {
              label: t('sv.rail.up'),
              icon: 'chev-up' as const,
              disabled: at <= 0,
              run: () => void moveGuild(guildId, at - 1),
            },
            {
              label: t('sv.rail.down'),
              icon: 'chev-down' as const,
              disabled: at < 0 || at >= list.length - 1,
              run: () => void moveGuild(guildId, at + 1),
            },
          ]),
      ...withSep(guildNotifyItems(guildId)),
      ...withSep(serverExitItems(guildId)),
    ];
  };
  return (
    <div
      className={
        'rail-item' +
        (sel ? ' sel' : '') +
        (unread.unread && !sel ? ' unread' : '') +
        (menuOpen ? ' menu' : '')
      }
      data-guild={guildId}
      data-drop={drag ?? undefined}
      data-dragging={lifted || undefined}
      onDragOver={(e) => {
        const id = useRailDrag.getState().id;
        if (!id || official || id === guildId) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const r = e.currentTarget.getBoundingClientRect();
        useRailDrag.setState({ over: guildId, after: e.clientY > r.top + r.height / 2 });
      }}
      onDragLeave={() => {
        if (useRailDrag.getState().over === guildId) useRailDrag.setState({ over: null });
      }}
      onDrop={(e) => {
        const { id, after } = useRailDrag.getState();
        useRailDrag.setState({ id: null, over: null });
        if (!id || official || id === guildId) return;
        e.preventDefault();
        const rest = movable().filter((x) => x !== id);
        void moveGuild(id, rest.indexOf(guildId) + (after ? 1 : 0));
      }}
    >
      <span className="rail-pill" aria-hidden="true" />
      <button
        type="button"
        className={'rail-btn srv' + (official ? '' : ' user') + (hasIcon ? ' has-icon' : '')}
        aria-label={label}
        data-tip={name}
        data-tip-pos="end"
        aria-current={sel ? 'page' : undefined}
        draggable={canDrag}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData(DRAG_TYPE, guildId);
          useRailDrag.setState({ id: guildId, over: null });
        }}
        onDragEnd={() => useRailDrag.setState({ id: null, over: null })}
        onClick={() => {
          if (!sel) goGuild(guildId);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenuOpen(true);
          openMenu(menuPoint(e), items(), { onClose: () => setMenuOpen(false) });
        }}
      >
        {/* the server's own icon once it has one (Server settings › Overview) */}
        <GuildIcon guildId={guildId} />
      </button>
      {unread.mentions && !sel ? (
        <b className="nx-badge rail-badge" aria-hidden="true">
          {fmt.num(unread.mentions)}
        </b>
      ) : null}
    </div>
  );
}
